import { PlusOutlined } from '@ant-design/icons';
import { FormErrorsAlert } from '@framework/form/form/errors/FormErrorsAlert.tsx';
import { FormHandler } from '@framework/form/form/FormHandler.tsx';
import { type RelationEditor } from '@framework/form/form/relations/useRelationEditor.ts';
import { Drawer } from '@framework/ui/components/drawer/Drawer.tsx';
import { DrawerButtonRow } from '@framework/ui/components/drawer/DrawerButtonRow.tsx';
import { Button, message, Space } from 'antd';
import { useCallback } from 'react';
import { type IMapGroup, MapGroupSchema } from '../layers/MapGroup.ts';
import { type IMapLayer } from '../layers/MapLayer.ts';
import { MapGroupConfigInput } from './MapGroupConfigInput.tsx';

export interface IMapGroupConfigDrawerButtonProps {
  groupEditor: RelationEditor<IMapGroup>;
  layersEditor: RelationEditor<IMapLayer>;
  validationContext?: Record<string, unknown>;
}

/**
 * Pulsante "Aggiungi Gruppo" e drawer per la creazione/modifica di un gruppo tematico.
 * Segue lo stesso pattern di `MapLayerConfigDrawerButton`.
 */
export function MapGroupConfigDrawerButton(
  props: IMapGroupConfigDrawerButtonProps,
) {
  const { groupEditor, layersEditor, validationContext } = props;

  const handleSubmit = useCallback(
    async (values: IMapGroup) => {
      try {
        const oldName = groupEditor.handle.current?.name;
        const type = groupEditor.handle.apply(values);

        if (type === 'updated' && oldName && values.name !== oldName) {
          const layers: IMapLayer[] = layersEditor.getValue() ?? [];
          const updated = layers.map(layer =>
            layer.group === oldName ? { ...layer, group: values.name } : layer,
          );
          layersEditor.replace(updated);
        }

        if (type === 'updated') {
          message.success('Gruppo modificato');
        } else {
          message.success('Gruppo aggiunto');
        }
      } catch (e) {
        message.error('Si è verificato un errore nel salvataggio del gruppo');
        console.error(e);
      }
    },
    [groupEditor, layersEditor],
  );

  return (
    <div>
      <Button
        type="dashed"
        block
        size="large"
        icon={<PlusOutlined aria-hidden="true" />}
        style={{ marginTop: 8 }}
        onClick={e => {
          groupEditor.handle.open(MapGroupSchema.cast({}, { assert: false }));
          e.stopPropagation();
        }}
      >
        Aggiungi Gruppo
      </Button>

      <Drawer
        title={groupEditor.handle.isNew ? 'Crea Gruppo' : 'Modifica Gruppo'}
        size="default"
        placement="right"
        destroyOnClose
        onClose={groupEditor.handle.cancel}
        open={groupEditor.handle.isVisible}
      >
        <FormHandler
          nested
          layout="vertical"
          schema={MapGroupSchema}
          validationContext={validationContext}
          initialValues={
            groupEditor.handle.current ??
            MapGroupSchema.cast({}, { assert: false })
          }
          onSubmit={handleSubmit}
        >
          <FormErrorsAlert />
          <MapGroupConfigInput />

          <DrawerButtonRow>
            <Space>
              <Button size="large" onClick={groupEditor.handle.cancel}>
                Annulla
              </Button>
              <Button type="primary" htmlType="submit" size="large">
                Applica
              </Button>
            </Space>
          </DrawerButtonRow>
        </FormHandler>
      </Drawer>
    </div>
  );
}
